import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DocsMarkdown } from './DocsMarkdown';
import { getPrevNext, type DocContent } from './docsData';

const contentModules = import.meta.glob<{ default: DocContent }>('./content/*.ts', { eager: true });

function getContent(slug: string): DocContent | null {
  const key = `./content/${slug}.ts`;
  const mod = contentModules[key];
  return mod?.default ?? null;
}

export default function DocPage() {
  const { slug } = useParams<{ slug: string }>();
  const doc = slug ? getContent(slug) : null;
  const nav = slug ? getPrevNext(slug) : { prev: undefined, next: undefined };

  if (!doc) {
    return (
      <div className="text-center py-20">
        <h1 className="text-2xl font-bold mb-2">Page Not Found</h1>
        <p className="text-muted-foreground">
          The documentation page you're looking for doesn't exist.
        </p>
        <Link to="/docs/connecting-to-snowflake" className="text-primary underline mt-4 inline-block">
          Go to docs home
        </Link>
      </div>
    );
  }

  return (
    <article>
      <h1 className="text-3xl font-bold tracking-tight mb-8">{doc.title}</h1>
      <DocsMarkdown content={doc.content} />

      {/* Prev / Next navigation */}
      <div className="flex items-center justify-between mt-12 pt-6 border-t border-border">
        {nav.prev ? (
          <Link
            to={`/docs/${nav.prev.slug}`}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            {nav.prev.title}
          </Link>
        ) : <div />}
        {nav.next ? (
          <Link
            to={`/docs/${nav.next.slug}`}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {nav.next.title}
            <ChevronRight className="h-4 w-4" />
          </Link>
        ) : <div />}
      </div>
    </article>
  );
}
